import * as React from "react";
import { ProgressBar } from "@fluentui/react-components";
import { REFINED } from "../../risikoanalyseStep/tokens";
import { progressLabel, progressValue } from "./statusProgress";
import { deriveVorgangStatus } from "./vorgangStatus";

const chip: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    padding: "2px 10px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: 600,
    whiteSpace: "nowrap",
    fontFamily: REFINED.fontUI,
};

/**
 * Farbpaar pro Status (einheitlich mit der Detail-Ansicht):
 *   Offen = neutral/lila (Aubergine) · Erledigt = grün.
 */
export const statusColors = (status: string): { bg: string; fg: string } => {
    switch (status) {
        case "Erledigt":
            return { bg: REFINED.okBg, fg: REFINED.ok };
        case "Offen":
        default:
            return { bg: REFINED.aub50, fg: REFINED.aub };
    }
};

/**
 * Vorgang-Status als farbige Pille. Primär-Darstellung des Status
 * in der Liste (Desktop-Tabelle + Karten) — dieselben Farben wie im
 * Detail-Kopf.
 */
export const VorgangStatusPill: React.FC<{ status: string; size?: "small" | "medium" }> = ({
    status,
    size = "medium",
}) => {
    const c = statusColors(status || "Offen");
    const pad = size === "small" ? "2px 9px" : "3px 11px";
    return (
        <span
            style={{
                ...chip,
                padding: pad,
                gap: 6,
                fontWeight: 700,
                backgroundColor: c.bg,
                color: c.fg,
            }}
        >
            <span style={{ width: 7, height: 7, borderRadius: 999, background: "currentColor" }} />
            {status || "Offen"}
        </span>
    );
};

/**
 * Schmale Zusatz-Zeile neben/unter der Status-Pille (Review D4): dünner
 * Fortschrittsbalken + „N/M erledigt". Rendert `null`, wenn es keine To-dos
 * gibt (keine sinnfreie „0/0"-Anzeige, SP14).
 */
export const VorgangProgressMini: React.FC<{ erledigt?: number | null; gesamt?: number | null }> = ({
    erledigt,
    gesamt,
}) => {
    const label = progressLabel(erledigt, gesamt);
    if (label == null) return null;
    return (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{ display: "inline-block", width: 52 }}>
                <ProgressBar value={progressValue(erledigt, gesamt)} thickness="medium" shape="rounded" />
            </span>
            <span style={{ fontSize: 11, color: REFINED.ink3, fontFamily: REFINED.fontUI, whiteSpace: "nowrap" }}>
                {label}
            </span>
        </span>
    );
};

interface Props {
    kind: "vorgang" | "todo";
    /** Status (Vorgang-Pille bzw. Todo-Chip); für Vorgänge sonst aus dem Fortschritt abgeleitet. */
    status?: string;
    /** Fortschritt (für kind="vorgang"). */
    erledigt?: number;
    gesamt?: number;
}

/**
 * Status-Darstellung (Review D4):
 *  - Vorgang → PRIMÄR die zweistufige Status-Pille (Offen /
 *    Erledigt); der Fortschrittsbalken bleibt nur klein als Zusatz-Zeile
 *    darunter (entfällt ganz ohne To-dos, keine „0/0"-Anzeige).
 *  - Todo → farbkodierter Status-Chip (read-only).
 */
export const StatusCell: React.FC<Props> = ({ kind, status, erledigt, gesamt }) => {
    if (kind === "vorgang") {
        const st = status ?? deriveVorgangStatus({ fortschrittErledigt: erledigt, fortschrittGesamt: gesamt });
        return (
            <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start", minWidth: 92 }}>
                <VorgangStatusPill status={st} size="small" />
                <VorgangProgressMini erledigt={erledigt} gesamt={gesamt} />
            </div>
        );
    }
    const c = statusColors(status ?? "Offen");
    return <span style={{ ...chip, backgroundColor: c.bg, color: c.fg }}>{status || "Offen"}</span>;
};
